import { useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useTaskStore } from '../../stores/taskStore';
import { AGENTS } from '../../utils/agents';
import TemplateCard from './TemplateCard';
import CreateTemplateModal from './CreateTemplateModal';
import type { TaskTemplate } from '../../types/tasks';

export default function TemplatesTab() {
  const templates = useTaskStore((s) => s.templates);
  const templatesLoading = useTaskStore((s) => s.templatesLoading);
  const fetchTemplates = useTaskStore((s) => s.fetchTemplates);

  const [showCreate, setShowCreate] = useState(false);
  const [editTemplate, setEditTemplate] = useState<TaskTemplate | null>(null);
  const [search, setSearch] = useState('');
  const [agentFilter, setAgentFilter] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filtered = templates.filter((t) => {
    if (agentFilter && t.assigned_agent !== agentFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-text-muted">
          {filtered.length} template{filtered.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Template
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md pl-9 pr-3 py-1.5 w-full text-sm focus:outline-none focus:border-accent-purple"
          />
        </div>
        <select
          value={agentFilter}
          onChange={(e) => setAgentFilter(e.target.value)}
          className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
        >
          <option value="">All Agents</option>
          {AGENTS.map((a) => (
            <option key={a.slug} value={a.slug}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      {/* Grid */}
      {templatesLoading ? (
        <div className="text-center py-12 text-text-muted text-sm">Loading templates...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-text-muted text-sm">
          {templates.length === 0
            ? 'No templates yet. Create your first template to get started.'
            : 'No templates match your filters.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => setEditTemplate(template)}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateTemplateModal onClose={() => setShowCreate(false)} />
      )}

      {editTemplate && (
        <CreateTemplateModal
          template={editTemplate}
          onClose={() => setEditTemplate(null)}
        />
      )}
    </div>
  );
}
